// BC-style "Find entries": type a document number and see every ledger entry and
// linked document that references it, grouped by type. Read-only drill-down.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, IconButton,
  CircularProgress, Chip, Divider, Table, TableHead, TableBody, TableRow, TableCell, Tooltip
} from '@mui/material';
import {
  Home, Search, ShoppingCart, Description, MoveToInbox, Payment,
  ReceiptLong, Inventory2, AccountBalance, AccountTree, RequestQuote, OpenInNew
} from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;

const ICON = {
  order: ShoppingCart, invoice: Description, receipt: MoveToInbox, payment: Payment,
  gl: AccountTree, vat: ReceiptLong, item: Inventory2, bank: AccountBalance,
  wht: ReceiptLong, quote: RequestQuote,
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const FindEntries = () => {
  const navigate = useNavigate();
  const [q, setQ]           = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const run = async (e) => {
    if (e) e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setLoad(true); setError(''); setResult(null);
    try {
      const { data } = await axios.get(`${API}/api/find/entries`, { params: { q: term }, headers });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Search failed');
    } finally {
      setLoad(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Search color="primary" />
              <Typography variant="h5" color="primary">Find Entries</Typography>
            </Box>
            <IconButton onClick={() => navigate('/finance')} title="Finance Home"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
              <Home />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter any document number — a purchase order (PO-…), invoice (PINV-…), voucher (PV-…),
            receipt (REC-/PREC-…) or student invoice (INV-…) — to see every ledger entry and linked
            document that references it.
          </Typography>

          <Box component="form" onSubmit={run} display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={1}>
            <TextField label="Document No." value={q} onChange={e => setQ(e.target.value)}
              size="small" sx={{ minWidth: 280 }} autoFocus placeholder="e.g. PINV-000004" />
            <Button type="submit" variant="contained" startIcon={<Search />} disabled={loading}>
              {loading ? 'Searching…' : 'Find'}
            </Button>
          </Box>

          {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
          {loading && <Box textAlign="center" py={4}><CircularProgress /></Box>}

          {result && !loading && (
            <Box mt={2}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" mb={2}>
                {result.totalHits > 0
                  ? <>Found <strong>{result.totalHits}</strong> entr{result.totalHits === 1 ? 'y' : 'ies'} for <strong>{result.query}</strong> across {result.groups.length} type{result.groups.length === 1 ? '' : 's'}.</>
                  : <>No entries found for <strong>{result.query}</strong>. Check the document number.</>}
              </Typography>

              {result.groups.map((g) => {
                const Icon = ICON[g.icon] || Description;
                return (
                  <Box key={g.type} mb={2.5}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Icon fontSize="small" sx={{ color: 'primary.main' }} />
                      <Typography variant="subtitle2" fontWeight={700}>{g.type}</Typography>
                      <Chip label={g.count} size="small" color="primary" variant="outlined" />
                      {g.path && (
                        <Tooltip title={`Open ${g.type} list`}>
                          <IconButton size="small" onClick={() => navigate(g.path)}>
                            <OpenInNew sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, py: 0.6 }}>No.</TableCell>
                            <TableCell sx={{ fontWeight: 700, py: 0.6 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 700, py: 0.6 }}>Description</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, py: 0.6 }}>Amount</TableCell>
                            <TableCell sx={{ fontWeight: 700, py: 0.6 }}>Type / Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {g.rows.map((r, i) => (
                            <TableRow key={i} hover>
                              <TableCell sx={{ py: 0.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{r.no}</TableCell>
                              <TableCell sx={{ py: 0.5, whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</TableCell>
                              <TableCell sx={{ py: 0.5, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                title={r.description}>{r.description}</TableCell>
                              <TableCell align="right" sx={{ py: 0.5, whiteSpace: 'nowrap' }}>{sym()} {fmt(r.amount)}</TableCell>
                              <TableCell sx={{ py: 0.5 }}>
                                {r.status && <Chip label={r.status} size="small" variant="outlined" />}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FindEntries;
